// src/hooks/useCachedData.js
import { useState, useEffect, useCallback, useRef } from 'react';
import { DashboardCache } from '../utils/dashboardCache';

/**
 * Custom hook for managing cached dashboard data
 * Provides instant loading from cache and background updates
 */
const useCachedData = (cacheKey, fetchFunction, options = {}) => {
  const {
    autoRefresh = true,
    refreshInterval = 300000, // 5 minutes
    cacheMaxAge = 300000, // 5 minutes
    backgroundUpdateDelay = 100, // 2 seconds delay for background update
  } = options;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isBackgroundUpdate, setIsBackgroundUpdate] = useState(false);
  const [error, setError] = useState(null);
  const backgroundUpdateTimeoutRef = useRef(null);
  const intervalRef = useRef(null);

  // Load data from cache or fetch from server
  const loadData = useCallback(async (isBackground = false) => {
    try {
      // First, try to load from cache for instant display
      if (!isBackground) {
        const cachedData = DashboardCache.getCache(cacheKey, cacheMaxAge);
        if (cachedData) {
          setData(cachedData);
          setLoading(false);
          
          // Schedule background update
          backgroundUpdateTimeoutRef.current = setTimeout(() => {
            setIsBackgroundUpdate(true);
            loadData(true);
          }, backgroundUpdateDelay);
          
          return;
        }
      }

      // Set loading state appropriately
      if (!isBackground) {
        setLoading(true);
      }
      setError(null);

      // Fetch fresh data
      const result = await fetchFunction();
      
      if (result && result.success && result.data !== undefined) {
        const newData = result.data;
        
        // Cache the new data
        DashboardCache.setCache(cacheKey, newData);
        
        // Update state
        setData(newData);
        setError(null);
      } else {
        throw new Error(result?.error || 'Failed to fetch data');
      }

    } catch (err) {
      console.error(`Failed to load data for ${cacheKey}:`, err);
      setError(err.message);
      
      // If this is initial load and we have cache, don't show error
      if (!isBackground) {
        const cachedData = DashboardCache.getCache(cacheKey, cacheMaxAge * 2); // Allow older cache on error
        if (cachedData !== null) {
          setData(cachedData);
          setError(null);
        }
      }
    } finally {
      setLoading(false);
      setIsBackgroundUpdate(false);
    }
  }, [cacheKey, fetchFunction, cacheMaxAge, backgroundUpdateDelay]);

  // Refresh data manually
  const refresh = useCallback(() => {
    setLoading(true);
    loadData(false);
  }, [loadData]);

  // Clear cache for this key
  const clearCache = useCallback(() => {
    DashboardCache.clearCache(cacheKey);
  }, [cacheKey]);

  // Initial load
  useEffect(() => {
    loadData(false);

    // Cleanup function
    return () => {
      if (backgroundUpdateTimeoutRef.current) {
        clearTimeout(backgroundUpdateTimeoutRef.current);
      }
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [loadData]);

  // Auto refresh
  useEffect(() => {
    if (!autoRefresh) return;

    intervalRef.current = setInterval(() => {
      loadData(true);
    }, refreshInterval);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [autoRefresh, refreshInterval, loadData]);

  return {
    data,
    loading,
    isBackgroundUpdate,
    error,
    refresh,
    clearCache,
    isCacheValid: DashboardCache.isCacheValid(cacheKey, cacheMaxAge),
  };
};

export default useCachedData;